import React, { useEffect, useState } from 'react';
import { Container, Box, Button, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import creditCardType from 'credit-card-type';
import toast from 'react-hot-toast';
import { RootState } from 'src/redux-store/appStore';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { httpRequest } from 'src/services/httpRequest';
import PaymentProcessingLoadinOverlay from 'src/components/common/PaymentProcessingLoadinOverlay';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import PaymentAcknowledgement from '../PaymentAcknowledgementPages';
import PaymentDetailsForm from './PaymentDetailsForm';
import ApplicationPaymentSummary from '../PaymentSummaryPages/ApplicationPaymentSummary';
import SubscriptionPaymentSummary from '../PaymentSummaryPages/SubscriptionPaymentSummary';
import { cardUtils } from './cardUtils';
import { PermitFeeType } from 'src/types/RoadPermit/roadPermit.type';
import { PaymentApplicationDetails, PaymentSummary } from '../types';
import SubscriptionPaymentAcknowledgement from '../PaymentAcknowledgementPages/SubscriptionPaymentAcknowledgement';
import { logoutUser } from 'src/utils/authService';
import CustomModal from 'src/ui-component/customModal/CustomModal';

const PaymentMethods: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );

  const isSubscriptionPayment =
    (location.state as { isSubscriptionPayment: boolean })
      ?.isSubscriptionPayment || false;
  const paymentSummary = (location.state as { paymentSummary: PaymentSummary })
    ?.paymentSummary;
  const applicationDetails = (
    location.state as { paymentApplicationDetails: PaymentApplicationDetails }
  )?.paymentApplicationDetails?.applicationDetails;
  const subscriptionDetails = (location.state as { subscriptionDetails: any })
    ?.subscriptionDetails;
  const isTreeReplacementPayment = (
    location.state as { isTreeReplacementPayment: boolean }
  )?.isTreeReplacementPayment;

  // Initialize states
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [showPaymentAcknowledgement, setShowPaymentAcknowledgement] =
    useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState<string | number>(
    applicationDetails?.applicationNumber
  );

  // Payment form states
  const [cardNumber, setCardNumber] = useState('');
  const [cardType, setCardType] = useState(null);
  const [cardNumberError, setCardNumberError] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryDateError, setExpiryDateError] = useState('');
  const [cvv, setCvv] = useState('');
  const [cvvError, setCvvError] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');

  // Calculate amounts
  const totalAmountWithoutTax = isSubscriptionPayment
    ? subscriptionDetails?.reduce((acc, permit) => acc + permit.Price, 0) || 0
    : paymentSummary?.fees
    ? Object.values(paymentSummary.fees).reduce((acc, fee) => acc + fee, 0)
    : 0;

  const [totalAmountPayable, setTotalAmountPayable] = useState(
    totalAmountWithoutTax
  );
  const [totalTaxAmount, setTotalTaxAmount] = useState(0);

  // Base path for navigation
  const basePath = `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}`;

  // Calculate total amount payable with tax
  useEffect(() => {
    if (!isSubscriptionPayment) {
      const totalTax = cardUtils.preciseToFixed(
        totalAmountWithoutTax * (paymentSummary?.taxRate / 100),
        2
      );
      const totalAmountPayableWithTax = cardUtils.preciseToFixed(
        totalAmountWithoutTax + parseFloat(totalTax),
        2
      );
      setTotalAmountPayable(parseFloat(totalAmountPayableWithTax));
      setTotalTaxAmount(parseFloat(totalTax));
    } else {
      const totalTax = cardUtils.preciseToFixed(
        (totalAmountWithoutTax * 2.15) / 100,
        2
      );
      setTotalTaxAmount(parseFloat(totalTax));
    }
  }, [totalAmountWithoutTax, paymentSummary?.taxRate, isSubscriptionPayment]);

  // Payment form handlers
  const handleCardNumberChange = (e) => {
    const formattedNumber = cardUtils.formatCardNumber(e.target.value);
    setCardNumber(formattedNumber);
    setCardType(cardUtils.getCardType(formattedNumber.replace(/\D/g, '')));
  };

  // Handle expiry date change
  const handleExpiryDateChange = (e) => {
    setExpiryDate(cardUtils.formatExpiryDate(e.target.value));
  };

  // Handle CVV change
  const handleCvvChange = (e) => {
    setCvv(e.target.value.replace(/\D/g, ''));
  };

  // Validation handlers
  const validateCardNumber = () => {
    const isValid = cardUtils.validateCardNumber(cardNumber);
    setCardNumberError(isValid ? '' : 'Please enter a valid card number');
    return isValid;
  };

  // Validate expiry date
  const validateExpiryDate = () => {
    const isValid = cardUtils.validateExpiryDate(expiryDate);
    setExpiryDateError(
      isValid ? '' : 'Please enter a valid expiry date (MM/YY)'
    );
    return isValid;
  };

  const validateCvv = () => {
    const isValid = cardUtils.validateCvv(cvv, cardType);
    setCvvError(isValid ? '' : 'Please enter a valid CVV');
    return isValid;
  };

  // Validate name on card
  const validateName = () => {
    const namePattern = /^[a-zA-Z]+(?: [a-zA-Z]+)*$/;

    if (!name.trim()) {
      setNameError('Please enter name on your card');
      return false;
    }

    if (!namePattern.test(name)) {
      setNameError(
        'Please enter a valid name (alphabetic characters only & no extra spaces)'
      );
      return false;
    }
    setNameError('');
    return true;
  };

  //Method to handle the fee type title
  const handleFeeTypeTitle = () => {
    if (paymentSummary?.fees?.buildingUCCFee) return 'Construction UCC Fee';
    if (
      paymentSummary?.fees?.treeReplacementFee &&
      currentPermitDetails?.id === 4
    )
      return 'Tree Replacement Fee';
    return paymentSummary?.fees?.applicationFee ? 'Application Fee' : '';
  };

  //Method to handle the other fee type
  const handleOtherFeeType = () => {
    return paymentSummary?.fees?.inspectionFee
      ? 'Inspection Fee'
      : paymentSummary?.fees?.additionalInspectionFee
      ? currentPermitDetails?.id === 2
        ? 'Additional Inspection Fee'
        : PermitFeeType.AdditionalInspectionFee
      : '';
  };

  //method to handle the tree replacement payment
  const handleTreeReplacementpayment = async () => {
    setLoading(true);
    const response = await httpRequest(
      'POST',
      `/tree_permit_application/${applicationDetails?.applicationId}/process-notify`,
      { VendorProcess: 'ReplacementFeePaid' }
    );
    if (response?.status === 'success') {
      setLoading(false);
      navigate(`${basePath}/application-list`);
    } else if (response.type === 'NETWORK_ERROR') {
      setLoading(false);
    } else {
      setLoading(false);
      toast.error(response?.message);
    }
  };

  const subtotal = subscriptionDetails?.reduce(
    (acc, permit) => acc + permit.Price,
    0
  );

  // Calculate tax amount
  const taxAmount = (subtotal * 2.15) / 100;

  // Calculate total with tax
  const total = subtotal + taxAmount;

  // Handle payment submission
  const handleProceedToPayment = async () => {
    if (
      validateCardNumber() &&
      validateExpiryDate() &&
      validateCvv() &&
      validateName()
    ) {
      setIsProcessing(true);
      try {
        const payload = isSubscriptionPayment
          ? {
              // Subscription payment payload
              CardDetails: {
                TotalAmount: total?.toFixed(2),
                CardNumber: cardNumber.replace(/\s+/g, ''),
                CardExpDate: expiryDate,
                CardCode: cvv
              },
              PermitsData: subscriptionDetails,
              CreditCardTaxRate: 2.15
            }
          : {
              // Application payment payload
              PermitID: currentPermitDetails?.id,
              ApplicationID: Number(applicationDetails?.applicationId),
              ApplicationFee: isTreeReplacementPayment
                ? paymentSummary?.fees?.treeReplacementFee
                : paymentSummary?.fees?.applicationFee ||
                  (paymentSummary?.fees?.buildingUCCFee ?? 0) +
                    (paymentSummary?.fees?.electricalUCCFee ?? 0) +
                    (paymentSummary?.fees?.fireUCCFee ?? 0) +
                    (paymentSummary?.fees?.mechanicalUCCFee ?? 0) +
                    (paymentSummary?.fees?.plumbingUCCFee ?? 0) +
                    (paymentSummary?.fees?.chimneyUCCFee ?? 0) ||
                  0,
              PermitFeeType: handleFeeTypeTitle(),
              InspectionFee:
                paymentSummary?.fees?.inspectionFee ||
                paymentSummary?.fees?.additionalInspectionFee ||
                0,
              PermitFeeType2: handleOtherFeeType(),
              TaxRateAmount: paymentSummary?.taxRate || 0,
              TotalAmount: totalAmountPayable,
              CardNumber: cardNumber.replace(/\s+/g, ''),
              CardExpDate: expiryDate,
              CardCode: cvv,
              CardHolderName: name
            };
        const endpoint = isSubscriptionPayment
          ? '/payments/subscription'
          : '/payments/creditCard';

        const response = await httpRequest('POST', endpoint, payload);

        if (response?.status === 'success') {
          if (isSubscriptionPayment) {
            // Handle successful subscription payment
            setIsProcessing(false);
            setIsPaymentSuccessful(true);
            toast.success('Subscription payment completed successfully!');
            setTimeout(() => {
              setIsPaymentSuccessful(false);
              setShowPaymentAcknowledgement(true);
            }, 2000);
          } else {
            // Handle successful application payment
            if (isTreeReplacementPayment) {
              await handleTreeReplacementpayment();
            }
            if (applicationDetails?.statusPayload === 'Issued') {
              // For pet permits (ID 10), only send basic fields
              // For all other permits, send construction fields as before
              let statusUpdatePayload: any;

              if (currentPermitDetails?.id === 10) {
                // Pet permit - only basic fields
                statusUpdatePayload = {
                  ApplicationID: applicationDetails?.applicationId,
                  ApplicationStatus: 'Issued'
                };
              } else {
                // All other permits - original behavior with construction fields
                statusUpdatePayload = {
                  ApplicationID: applicationDetails?.applicationId,
                  ApplicationStatus: 'Issued',
                  CompanyName: 'ABC Builders',
                  ProjectLocation: 'Orlando',
                  ApplicationType: 'Home Owner',
                  ProjectContactName: 'Denial'
                };
              }

              const statusResponse = await httpRequest(
                'PUT',
                `/applications/${currentPermitDetails?.id}/update_status`,
                statusUpdatePayload
              );

              if (statusResponse?.status === 'success') {
                setIsProcessing(false);
                setIsPaymentSuccessful(true);
                toast.success('Payment completed successfully!');
                setTimeout(() => {
                  setIsPaymentSuccessful(false);
                  setShowPaymentAcknowledgement(true);
                }, 2000);
              } else {
                setIsProcessing(false);
                if (statusResponse?.type !== 'NETWORK_ERROR') {
                  toast.error(statusResponse?.message);
                }
              }
            } else if (applicationDetails?.applicationStatus === 'Draft') {
              const statusUpdatePayload = {
                ApplicationID: applicationDetails?.applicationId,
                ApplicationStatus: applicationDetails?.statusPayload,
                PermitID: currentPermitDetails?.id,
                PermitPrefix: applicationDetails?.permitPrefix
              };

              const statusResponse = await httpRequest(
                'POST',
                '/payments/update_status',
                statusUpdatePayload
              );

              if (statusResponse?.status === 'success') {
                setApplicationNumber(statusResponse?.data?.ApplicationNumber);
                setIsProcessing(false);
                setIsPaymentSuccessful(true);
                toast.success('Payment completed successfully!');
                setTimeout(() => {
                  setIsPaymentSuccessful(false);
                  setShowPaymentAcknowledgement(true);
                }, 2000);
              } else {
                setIsProcessing(false);
                if (statusResponse.type !== 'NETWORK_ERROR') {
                  toast.error(statusResponse?.message);
                }
              }
            } else {
              setIsProcessing(false);
              setIsPaymentSuccessful(true);
              toast.success('Payment completed successfully!');
              setTimeout(() => {
                setIsPaymentSuccessful(false);
                setShowPaymentAcknowledgement(true);
              }, 2000);
            }
          }
        } else if (response.type === 'NETWORK_ERROR') {
          setIsProcessing(false);
        } else {
          toast.error(response?.message);
          dispatch(handleError(response?.message));
          setIsProcessing(false);
        }
      } catch (error) {
        setIsProcessing(false);
        const errorMessage =
          error.message ||
          'An unexpected error occurred. Please try again later.';
        toast.error(errorMessage);
        dispatch(handleError(errorMessage));
      }
    }
  };

  const handleRelogin = () => {
    // Handle relogin logic
    logoutUser(dispatch, navigate);
  };

  const handleCancelPayment = () => {
    toast.success('Payment Cancelled!');
    if (isSubscriptionPayment) navigate(`/subscriptions`);
    else navigate(`${basePath}/draft-application`);
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <>
      <Container
        component="main"
        maxWidth={showPaymentAcknowledgement ? 'md' : 'sm'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: { xs: 1.5, sm: 3 }
        }}
      >
        {!showPaymentAcknowledgement && (
          <>
            {isSubscriptionPayment ? (
              <SubscriptionPaymentSummary
                subscriptionDetails={subscriptionDetails}
                taxRate={2.15}
              />
            ) : (
              <ApplicationPaymentSummary
                paymentSummary={paymentSummary}
                totalTaxAmount={totalTaxAmount}
                totalAmountPayable={totalAmountPayable}
                totalAmountWithoutTax={totalAmountWithoutTax}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                borderRadius: 2,
                boxShadow: 3,
                padding: { xs: 1.5, sm: 3 },
                mt: { xs: 1.5, sm: 4 },
                border: '1px solid #e0e0e0',
                backgroundColor: '#ffffff'
              }}
            >
              <PaymentDetailsForm
                cardNumber={cardNumber}
                handleCardNumberChange={handleCardNumberChange}
                cardNumberError={cardNumberError}
                validateCardNumber={validateCardNumber}
                cardTypeLogo={
                  cardType ? cardUtils.getCardTypeLogo(cardType) : null
                }
                cardType={cardType}
                expiryDate={expiryDate}
                handleExpiryDateChange={handleExpiryDateChange}
                expiryDateError={expiryDateError}
                validateExpiryDate={validateExpiryDate}
                cvv={cvv}
                handleCvvChange={handleCvvChange}
                cvvError={cvvError}
                validateCvv={validateCvv}
                maxLength={
                  creditCardType(cardNumber.replace(/\s/g, ''))[0]?.type ===
                  'american-express'
                    ? 4
                    : 3
                }
                name={name}
                setName={setName}
                nameError={nameError}
                validateName={validateName}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mt: 3 }}
                width={{ xs: '100%', sm: 'auto' }}
              >
                <Button
                  variant="contained"
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    backgroundColor: isHovered ? '#8B0000' : '#e30000',
                    color: 'white'
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onClick={() => setIsOpen(true)}
                >
                  Cancel Payment
                </Button>
                <Button
                  sx={{
                    width: { xs: '100%', sm: 'auto' }
                  }}
                  variant="contained"
                  color="primary"
                  onClick={handleProceedToPayment}
                >
                  Proceed to Payment
                </Button>
              </Stack>
            </Box>
          </>
        )}
        {showPaymentAcknowledgement && !isSubscriptionPayment && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: 2,
              boxShadow: 3,
              padding: { xs: 1.5, sm: 3 },
              mt: 4,
              border: '1px solid #e0e0e0',
              backgroundColor: '#ffffff'
            }}
          >
            {/* Payment Acknowledgement */}
            <PaymentAcknowledgement
              showPaymentAcknowledgement={showPaymentAcknowledgement}
              applicationDetails={applicationDetails}
              totalAmountPayable={totalAmountPayable}
              totalAmountWithoutTax={totalAmountWithoutTax}
              totalTaxAmount={totalTaxAmount}
              applicationNumber={applicationNumber}
              basePath={basePath}
              currentPermitDetails={currentPermitDetails}
            />
          </Box>
        )}
        <SubscriptionPaymentAcknowledgement
          showPaymentAcknowledgement={
            isSubscriptionPayment && showPaymentAcknowledgement
          }
          applicationDetails={subscriptionDetails}
          totalAmountPayable={total?.toFixed(2)}
          totalAmountWithoutTax={totalAmountWithoutTax}
          totalTaxAmount={totalTaxAmount}
          taxRate={2.15}
          onRelogin={handleRelogin}
        />
      </Container>

      <PaymentProcessingLoadinOverlay
        isProcessing={isProcessing}
        isSuccess={isPaymentSuccessful}
      />
      <CustomModal
        title={'Cancel Payment?'}
        IsOpen={IsOpen}
        setIsOpen={setIsOpen}
        onSave={handleCancelPayment}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>Are you sure you want to cancel the payment?</Typography>
        }
      />
    </>
  );
};

export default PaymentMethods;
